import { createContext, useContext, useEffect, useMemo, useRef, useState } from "react"
import { useToggle } from "react-use"
import { useLocation, useNavigate } from "react-router-dom"

import { USER_ACCOUNT_LOADING_STATUS } from "../ClientAuthorization/Default/common/constant"

import { Psychologist } from "utils/interfaces"
import { useClientContext } from "modules/ClientAuthorization/common/ClientContext"
import { Analytics } from "utils/analytics"
import { findFastBookingWithPsychologist, getHasSlots, getParameterFromUrl } from "utils/helpers"
import { Pages, PAYMENT_DONE_PARAMETER } from "modules/BookingCommon/common/constants"
import {
    getDefaultServiceGroup,
    getIsBookingAvailable,
    getLastServiceGroup,
    getOnlineAndOfflineService,
    getOnlineOrOfflineDate,
    getOnlineOrOfflineFromSurvey,
    getServiceGroupFromSurvey,
} from "modules/BookingCommon/common/utils"
import {
    createdBookingLoader,
    createdDepositLoader,
    createdTimeRequestLoader,
    DEPOSIT_QUERY_KEY,
} from "modules/BookingCommon/common/queries"
import { CURRENCY } from "utils/constants"
import { useBooking } from "modules/BookingCommon/common/hooks"
import { PaymentStatus } from "components/ClientCardAttachment/constants"
import { updateCurrency } from "modules/BookingCommon/common/api"
import {
    getAmountOfPages,
    getOnlineOrOfflineFromServices,
    getTimeRequestSelectorsDefault,
} from "modules/Booking/common/utils"
import { SESSION_TYPE } from "modules/Booking/common/constants"
import { Header } from "modules/Booking/Header"
import { Heading } from "modules/Booking/Heading"
import { Content } from "modules/Booking/Content"
import {
    ContextHandlers,
    ContextValue,
    DepositConfiguration,
    PossibleHoursRecord,
    SessionTypePartial,
    TimeRequestSelectors,
    UtcTime,
} from "modules/Booking/common/interfaces"
import { useGetCurrentPage } from "modules/Booking/common/hooks/useGetCurrentPage"
import { useOnlyFreeSessions } from "pages/FreePsysPage/useOnlyFreeSessions"
import { ServicesGroup } from "utils/helpers/ServicesGroup"
import { useSearchParams } from "utils/customHooks"
import { UserAccount } from "utils/UserAccount"
import { queryClient } from "utils/infrastructure/query-client"

const Context = createContext<[ContextValue, ContextHandlers] | null>(null)

function getInitialPage(data: {
    skipCards: boolean
    forcePaymentPage: boolean
    defaultPage: Pages
}) {
    if (data.forcePaymentPage) return Pages.PAYMENT
    if (data.skipCards) return Pages.SESSION_DATE
    return data.defaultPage
}

function BookingProvider({
    psy,
    deposits,
}: {
    psy: Psychologist
    deposits?: DepositConfiguration[] | undefined
}) {
    const searchParams = useSearchParams()
    const navigate = useNavigate()
    const location = useLocation()

    const onlyFree = useOnlyFreeSessions()
    const [{ isVerified, userAccount, loading: clientLoading }] = useClientContext()
    const serviceGroups = ServicesGroup.from(psy, onlyFree)

    const isBookingAvailable = getIsBookingAvailable(serviceGroups)
    const hasSlots = getHasSlots(serviceGroups)

    const lastFastBookingPsychologist = findFastBookingWithPsychologist(
        userAccount.psychologists,
        psy.hash_id
    )
    const hasPreviousSessions = userAccount.psychologists.length > 0
    const hasPreviousSessionsWithSamePsychologist = !!lastFastBookingPsychologist

    const [serviceGroupId] = useState<number | undefined>(location.state?.serviceGroupId)

    const lastServiceGroup = getLastServiceGroup(
        serviceGroupId || lastFastBookingPsychologist?.last_booking_service_type_id,
        serviceGroups
    )

    const lastServiceAvailable = hasSlots
        ? lastServiceGroup?.availableStatus.ok
        : lastServiceGroup?.availableStatusWithCompany.ok

    const lastService =
        lastServiceAvailable && lastFastBookingPsychologist?.last_booking_service_type_id
            ? ServicesGroup.findService(
                  serviceGroups,
                  lastFastBookingPsychologist?.last_booking_service_type_id
              )
            : undefined
    const groupFromSurvey = getServiceGroupFromSurvey(serviceGroups)

    const [, toggleChooseFlowWithNoAvailable] = useToggle(false)

    const amountOfPages = getAmountOfPages(serviceGroups, isBookingAvailable, lastService, hasSlots)
    const initialPage = getInitialPage({
        skipCards: !!(lastServiceGroup && lastServiceAvailable),
        forcePaymentPage: !!createdBookingLoader.getHash(),
        defaultPage: amountOfPages[0],
    })
    const currentPage = useGetCurrentPage()

    const {
        selectedTime,
        changeSelectedTime,
        slotId,
        setSlotId,
        slotDuration,
        setSlotDuration,
        serviceGroup,
        setServiceGroup,
        onlineOrOffline,
        setOnlineOrOffline,
        selectedService,
        setSelectedService,
        datePickerError,
        setDatePickerError,
        datePickerIsLoaded,
        toggleDatePickerIsLoaded,
        currentWeek,
        setCurrentWeek,
        indexOfCurrentPage,
        nextPage,
        setPage,
    } = useBooking({
        initialPage,
        amountOfPages,
        isBookingAvailable,
    })

    const [errorMessage, setErrorMessage] = useState<string | undefined>()
    const [{ email }, setUserAccountData] = useState({
        email: UserAccount.email,
    })
    const [currency, setCurrency] = useState<CURRENCY>(CURRENCY.RUB)
    const [selectedDiscount, setSelectedDiscount] = useState(
        psy.discount && !isVerified ? psy.discount.id : undefined
    )
    const [possibleHours, setPossibleHours] = useState<PossibleHoursRecord | null>(null)
    const [timeRequestsUtcTime, setTimeRequestsUtcTime] = useState<UtcTime | null>(null)

    const [requestTimesSelectors, setTimeRequestsSelectors] = useState<TimeRequestSelectors[]>(
        getTimeRequestSelectorsDefault()
    )
    const [timeRequestModalActive, toggleTimeRequestModalActive] = useToggle(false)
    const [canOpenTimeRequestModal, toggleCanOpenTimeRequestModal] = useToggle(true)

    const [sessionType, setSessionType] = useState<SessionTypePartial>(SESSION_TYPE.DEFAULT)
    const [isChooseManuallyService, setIsChooseManuallyService] = useState(false)
    const [canChangeHistory, setCanChangeHistory] = useToggle(true)
    const [selectDeposit, setSelectDeposit] = useState<DepositConfiguration | undefined>(
        deposits ? deposits[0] : undefined
    )

    const useBookingDialogAnalyticsOneTimeRef = useRef(true)
    const position = searchParams.get("position") ?? "1"
    const orderHash = searchParams.get("order_hash")
    const bookingHash = createdBookingLoader.getHash()
    const { data: bookingDetails } = createdBookingLoader.useGet(bookingHash)
    createdBookingLoader.useGetUpdated(
        getParameterFromUrl(PAYMENT_DONE_PARAMETER) === "1" ? bookingHash : undefined,
        bookingDetails?.status
    )
    const [isLoading, toggleIsLoading] = useToggle(false)
    const [hash, setHash] = useState<string>()

    const depositHash = createdDepositLoader.getHash()
    const depositResponse = createdDepositLoader.useGet(depositHash)
    const depositPaymentStatusResponse = createdDepositLoader.useGetPaymentStatus(
        getParameterFromUrl(PAYMENT_DONE_PARAMETER) === "1" ? depositHash : undefined,
        orderHash,
        depositResponse.data?.status
    )

    createdDepositLoader.useGetUpdatedPaymentStatus(
        getParameterFromUrl(PAYMENT_DONE_PARAMETER) === "1" ? depositHash : undefined,
        orderHash,
        depositPaymentStatusResponse.data?.status,
        depositResponse.data?.status
    )

    const timeRequestHash = createdTimeRequestLoader.getHash()
    createdTimeRequestLoader.useGetUpdated(
        getParameterFromUrl(PAYMENT_DONE_PARAMETER) === "1" ? timeRequestHash : undefined,
        createdTimeRequestLoader.useGet(timeRequestHash).data?.status
    )

    useEffect(() => {
        const online = onlineOrOffline !== undefined ? onlineOrOffline === "online" : undefined

        setSelectedService(
            onlyFree
                ? serviceGroup?.services[0]
                : serviceGroup?.services.find((service) => {
                      return service.online === online
                  })
        )
        setCurrentWeek(getOnlineOrOfflineDate(onlyFree ? true : online, psy.available_for_booking))
    }, [serviceGroup, onlineOrOffline, psy])

    useEffect(() => {
        if (
            depositPaymentStatusResponse.data?.status === PaymentStatus.WAITING_FOR_CAPTURE ||
            depositPaymentStatusResponse.data?.status === PaymentStatus.PAID
        ) {
            void queryClient.refetchQueries([DEPOSIT_QUERY_KEY, depositHash])
        }
    }, [depositPaymentStatusResponse.data?.status])

    useEffect(() => {
        if (!isBookingAvailable) return

        UserAccount.executeOnceWhenSignedIn(() => {
            setUserAccountData({
                email: UserAccount.email,
            })
        })
    }, [isBookingAvailable])

    useEffect(() => {
        if (
            clientLoading !== USER_ACCOUNT_LOADING_STATUS.LOADING &&
            canChangeHistory &&
            !(bookingHash || timeRequestHash || depositHash)
        ) {
            const url = location.pathname + location.search
            if (
                (lastFastBookingPsychologist && lastServiceGroup?.availableStatus.ok) ||
                (serviceGroupId && lastServiceGroup?.availableStatus.ok)
            ) {
                navigate(url, { state: { bookingPage: amountOfPages[0] }, replace: true })
                navigate(url, { state: { bookingPage: amountOfPages[0] } })

                if (amountOfPages[0] !== Pages.SESSION_DATE) {
                    navigate(url, { state: { bookingPage: amountOfPages[1] } })
                }
            } else {
                navigate(url, { state: { bookingPage: amountOfPages[0] }, replace: true })
            }
        }
    }, [clientLoading])

    useEffect(() => {
        if (isChooseManuallyService) return
        setServiceGroup(
            getDefaultServiceGroup({
                lastUsed: lastServiceGroup,
                fromSurvey: groupFromSurvey,
                defaultGroup: serviceGroups.find((group) =>
                    hasSlots ? group.availableStatus.ok : group.availableStatusWithCompany
                ),
                bookingAvailable: hasSlots,
            })
        )
    }, [isChooseManuallyService, lastServiceGroup?.id])

    useEffect(() => {
        if (!serviceGroup) return
        if (onlyFree) return setOnlineOrOffline("online")
        const { onlineService, offlineService } = getOnlineAndOfflineService(
            serviceGroup,
            !hasSlots
        )
        const surveyOnlineOrOffline = getOnlineOrOfflineFromSurvey(serviceGroup, !hasSlots)
        const lastOfflineOrOnline = lastService
            ? getOnlineOrOfflineFromServices(serviceGroup, lastService.online, !hasSlots)
            : undefined
        const onlineOrOfflineDefault =
            lastOfflineOrOnline ||
            surveyOnlineOrOffline ||
            (onlineService ? "online" : offlineService ? "offline" : undefined)
        setOnlineOrOffline(onlineOrOfflineDefault)
    }, [serviceGroup])

    useEffect(() => {
        if (serviceGroups.length !== 1) {
            return
        }

        Analytics.psychologist_service_selected({
            service_type: serviceGroup?.name,
            psy,
        })
    }, [serviceGroup])

    useEffect(() => {
        if (!hasSlots && serviceGroupId && canOpenTimeRequestModal) {
            toggleTimeRequestModalActive(true)
            toggleCanOpenTimeRequestModal(false)
            setSlotDuration(slotDuration)
        }
    }, [hasSlots, serviceGroupId, canOpenTimeRequestModal])

    useEffect(() => {
        if (isVerified && currentPage !== Pages.FORM) setSelectedDiscount(undefined)

        if (
            currentPage !== Pages.PAYMENT ||
            !bookingDetails?.booking_id ||
            !bookingDetails.need_to_pay
        )
            return

        updateCurrency(bookingDetails.order_hash, currency)
            .then(({ payment_system, price_usd }) => {
                createdBookingLoader.updateData(bookingDetails.booking_hash, {
                    payment_system,
                    price_usd,
                })
            })
            .catch(undefined)
    }, [currentPage])

    useEffect(() => {
        setErrorMessage(undefined)
    }, [email])

    useEffect(() => {
        if ((bookingHash || timeRequestHash || depositHash) && currentPage !== Pages.PAYMENT) {
            setPage(Pages.PAYMENT)
        }
    }, [])

    useEffect(() => {
        if (bookingHash && !useBookingDialogAnalyticsOneTimeRef.current) {
            return
        }

        if (onlyFree) {
            Analytics.booking_dialog_free({ psy, bookingHash, position })
        } else {
            Analytics.booking_dialog({ psy, bookingHash, position })
        }
        useBookingDialogAnalyticsOneTimeRef.current = false
    }, [bookingHash])

    function handleClickButtonInCards() {
        setIsChooseManuallyService(true)
        if (!hasSlots) {
            toggleTimeRequestModalActive()
            toggleChooseFlowWithNoAvailable(true)
            setSlotDuration(slotDuration)
        } else {
            nextPage()
        }
    }

    const data: ContextValue = {
        pagesCount: amountOfPages.length,
        psy,
        deposits,
        selectedTime,
        slotId,
        serviceGroups,
        serviceGroup,
        onlineOrOffline,
        datePickerError,
        datePickerIsLoaded,
        slotDuration,
        email,
        selectedDiscount,
        selectedService,
        indexOfCurrentPage,
        currency,
        errorMessage,
        requestTimesSelectors,
        timeRequestModalActive,
        possibleHours,
        currentWeek,
        sessionType,
        isOnline: onlineOrOffline === "online",
        timeRequestsUtcTime,
        hasPreviousSessions: hasPreviousSessions,
        hasPreviousSessionsWithSamePsychologist,
        isUserVerified: isVerified,
        hasSlots,
        isLoading,
        selectDeposit,
        hash,
    }

    const handlers: ContextHandlers = useMemo(
        () => ({
            nextPage,
            setPage,
            changeSelectedTime,
            setDatePickerError,
            toggleDatePickerIsLoaded,
            setSlotId,
            setSlotDuration,
            setServiceGroup,
            changeOnlineOrOffline: (isOnlineOrOffline) => {
                setOnlineOrOffline(isOnlineOrOffline)
                setTimeRequestsSelectors(getTimeRequestSelectorsDefault())
            },
            setSelectedDiscount,
            setCurrency,
            changeSessionType: (sessionType) => {
                if (sessionType === SESSION_TYPE.TIME_REQUEST) {
                    setCurrency(CURRENCY.RUB)
                }
                setSessionType(sessionType)
            },
            setErrorMessage,
            setTimeRequestsSelectors,
            toggleTimeRequestModalActive,
            setPossibleHours,
            setCurrentWeek,
            setTimeRequestsUtcTime,
            setIsChooseManuallyService,
            handleClickButtonInCards,
            toggleChooseFlowWithNoAvailable,
            toggleIsLoading,
            setSelectDeposit,
            setHash,
            setCanChangeHistory,
        }),
        [amountOfPages, currentPage, isBookingAvailable, hasPreviousSessionsWithSamePsychologist]
    )

    return (
        <Context.Provider value={[data, handlers]}>
            <Header />
            <Heading />
            <Content />
        </Context.Provider>
    )
}

const useBookingContext = (): [ContextValue, ContextHandlers] => {
    const currentContext = useContext(Context)

    if (!currentContext) {
        throw new Error("useBookingContext has to be used within <BookingContext.Provider>")
    }

    return currentContext
}

export { useBookingContext, BookingProvider }
